import React, { useEffect, useState } from 'react'
import { Form } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/Button'
import { Label } from '@/components/Label'
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/Modal'
import { Select } from '@/components/Select'
import { TextField } from '@/components/TextField'
import { Switch } from '@/components/Switch'

type Props = {
  defaultContext: string
  dbContexts: Record<string, string>
  createUser: (user: NewUser, changePassword: boolean) => void
}

type NewUser = {
  dbContext: string
  email: string
  isTrialUser: boolean
  isGamebusEnabled: boolean
  livingIndependently: boolean
  name: string
  password: string
}

export default function CreateUserDialog({ defaultContext, dbContexts, createUser }: Props) {
  const { t } = useTranslation()
  const [user, setUser] = useState<NewUser>({
    dbContext: defaultContext,
    email: '',
    isTrialUser: true,
    isGamebusEnabled: false,
    livingIndependently: true,
    name: '',
    password: '',
  })
  const [changePassword, setChangePassword] = useState(false)

  useEffect(() => {
    setUser({
      ...user,
      dbContext: defaultContext,
    })
  }, [defaultContext])

  const contextOptions = Object.entries(dbContexts).map(([key, value]) => ({
    value: key,
    label: value,
  }))

  const handleChangeInput = (key: string, value?: unknown) => {
    setUser({
      ...user,
      [key]: value,
    })
  }

  return (
    <DialogTrigger>
      <Button variant="primary">{t('users.createUser')}</Button>
      <Modal style={{ width: 400, display: 'flex', flexDirection: 'column' }}>
        <Dialog style={{ flex: 1 }}>
          {({ close }) => (
            <>
              <ModalHeader onClose={close}>{t('users.createUser')}</ModalHeader>

              <ModalContent style={{ height: '100%' }}>
                <Form>
                  <div style={{ marginBottom: 10 }}>
                    <Label>{t('users.dbContext')}</Label>
                    <Select
                      value={{ value: user.dbContext, label: dbContexts[user.dbContext] }}
                      defaultValue={{ value: defaultContext, label: dbContexts[defaultContext] }}
                      options={contextOptions}
                      onChange={(selection) => handleChangeInput('dbContext', selection?.value)}
                    />
                  </div>

                  <TextField
                    autoFocus
                    onChange={(value) => handleChangeInput('email', value)}
                    label={t('users.email')}
                    name="email"
                    type="text"
                    isRequired
                  />

                  <TextField
                    onChange={(value) => handleChangeInput('password', value)}
                    label={t('users.newPassword')}
                    name="password"
                    type="password"
                    isRequired
                  />

                  <Switch
                    isSelected={changePassword}
                    onChange={(value) => setChangePassword(value)}>
                    {t('users.requestPasswordChange')}
                  </Switch>

                  <Switch
                    isSelected={user.isTrialUser}
                    onChange={(value) => handleChangeInput('isTrialUser', value)}>
                    {t('users.isTrialUser')}
                  </Switch>

                  <Switch
                    isSelected={user.isGamebusEnabled}
                    onChange={(value) => handleChangeInput('isGamebusEnabled', value)}>
                    {t('users.isGamebusEnabled')}
                  </Switch>
                </Form>
              </ModalContent>

              <ModalFooter onClose={close}>
                <Button
                  variant="primary"
                  onPress={() => {
                    createUser(user, changePassword)
                    close()
                  }}>
                  {t('users.saveUser')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Dialog>
      </Modal>
    </DialogTrigger>
  )
}
